import { z } from 'zod';

// Community reviews are hidden from public pages until there is enough traffic to fill them.
// Stored reviews, the reviews API and editorial moderation are unaffected; set to true to restore them.
export const reviewsVisible = false;

// Reviewers pick from the accord vocabulary the catalogue's scent profiles already use,
// so personal profiles stay comparable with editorial ones.
export const accordGroups = [
  {
    name: 'Fresh',
    accords: [
      'Citrus',
      'Fresh',
      'Green',
      'Aquatic',
      'Aromatic',
      'Tea',
      'Mineral',
      'Salty',
      'Aldehydic',
    ],
  },
  {
    name: 'Floral and fruity',
    accords: ['Floral', 'Rose', 'Fruity', 'Powdery'],
  },
  {
    name: 'Sweet and gourmand',
    accords: [
      'Sweet',
      'Vanilla',
      'Caramel',
      'Honey',
      'Chocolate',
      'Coffee',
      'Coconut',
      'Nutty',
      'Creamy',
      'Boozy',
    ],
  },
  {
    name: 'Warm and woody',
    accords: [
      'Woody',
      'Warm Spicy',
      'Amber',
      'Resinous',
      'Oud',
      'Earthy',
      'Smoky',
      'Tobacco',
      'Leather',
      'Musky',
      'Animalic',
    ],
  },
] as const;
const accordKey = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');
const accordAliases: Record<string, string> = { leathery: 'Leather' };
const knownAccords = new Map<string, string>(
  accordGroups.flatMap((group) => group.accords.map((name) => [accordKey(name), name] as const)),
);
/** The vocabulary spelling of an accord name, or null when it is not in the vocabulary. */
export function canonicalAccord(name: string) {
  const key = accordKey(name);
  return knownAccords.get(key) ?? accordAliases[key] ?? null;
}

// Performance scales follow the longevity and sillage vocabulary fragrance reviewers already use.
export const longevityLevels = [
  { value: 1, label: 'Very weak', detail: 'Under 1 hour' },
  { value: 2, label: 'Weak', detail: '1–3 hours' },
  { value: 3, label: 'Moderate', detail: '3–6 hours' },
  { value: 4, label: 'Long lasting', detail: '6–12 hours' },
  { value: 5, label: 'Very long lasting', detail: '12+ hours' },
] as const;
export const sillageLevels = [
  { value: 1, label: 'Intimate', detail: 'Close to the skin' },
  { value: 2, label: 'Moderate', detail: 'Within arm’s length' },
  { value: 3, label: 'Strong', detail: 'Fills a room' },
  { value: 4, label: 'Enormous', detail: 'Noticed from afar' },
] as const;

export const reviewSchema = z
  .object({
    sentiment: z.enum(['POSITIVE', 'NEGATIVE']),
    body: z
      .string()
      .trim()
      .min(20, 'Write at least 20 characters about your experience.')
      .max(3000),
    accords: z
      .array(
        z.object({
          name: z.string().transform((name, context) => {
            const canonical = canonicalAccord(name);
            if (!canonical)
              context.addIssue({
                code: 'custom',
                message: 'Choose accords from the list.',
              });
            return canonical ?? name;
          }),
          intensity: z.number().int().min(0).max(5),
        }),
      )
      .max(12)
      .default([]),
    longevity: z.number().int().min(1).max(longevityLevels.length).nullable().default(null),
    sillage: z.number().int().min(1).max(sillageLevels.length).nullable().default(null),
  })
  .superRefine((value, context) => {
    if (/\b(?:dupe|clone|copy|replica)\s+of\b|\b(?:alternative|version)\s+to\b|\b(?:smells?\s+like|inspired\s+by)\b/i.test(value.body))
      context.addIssue({
        code: 'custom',
        message: 'Describe this fragrance on its own merits, without comparing it to another product.',
        path: ['body'],
      });
    const names = value.accords.map((a) => a.name);
    if (new Set(names).size !== names.length)
      context.addIssue({
        code: 'custom',
        message: 'Use each accord only once.',
        path: ['accords'],
      });
  });
export type ReviewInput = z.infer<typeof reviewSchema>;
export type FragranceReview = ReviewInput & {
  id: string;
  createdAt: string;
  updatedAt: string;
  user: { username: string; avatarUrl: string | null };
};
export type ReviewPage = {
  items: FragranceReview[];
  total: number;
  positive: number;
  negative: number;
  page: number;
  own: FragranceReview | null;
};
